/**
 * LoginPage
 */

import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { Lock, User, AlertCircle } from "lucide-react";
import { log } from "@/shared/utils/logger";
import { AuthEntryShell } from "./components/AuthEntryShell";
import { AuthErrorSummary } from "./components/AuthErrorSummary";
import { AuthenticatedAuthNotice } from "./components/AuthenticatedAuthNotice";
import { LoginFormInput } from "./components/LoginFormInput";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { cn, useTheme } from "@/ui/theme/ThemeContext";

export const LoginPage: React.FC = () => {
  const { t } = useSettings();
  const { theme: appTheme } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, logout, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const errorSummaryRef = useRef<HTMLDivElement | null>(null);
  const serverErrorRef = useRef<HTMLDivElement | null>(null);

  const returnPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawReturnTo = params.get("returnTo") || "/";
    return rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//") ? rawReturnTo : "/";
  }, [location.search]);

  const signupPath = useMemo(
    () => `/signup?returnTo=${encodeURIComponent(returnPath)}`,
    [returnPath]
  );

  const summaryErrors = useMemo(
    () =>
      [
        fieldErrors.username ? { id: "username", message: fieldErrors.username } : null,
        fieldErrors.password ? { id: "password", message: fieldErrors.password } : null,
      ].filter(Boolean) as Array<{ id: string; message: string }>,
    [fieldErrors.password, fieldErrors.username]
  );

  const validateFields = () => {
    const nextErrors: { username?: string; password?: string } = {};

    if (!username.trim()) {
      nextErrors.username = t("Enter your username or email");
    }

    if (!password.trim()) {
      nextErrors.password = t("Enter your password");
    }

    return nextErrors;
  };

  const getFriendlyLoginError = (message: string) => {
    const normalized = message.trim().toLowerCase();

    if (normalized.includes("invalid credentials")) {
      return t(
        "Invalid username or password. Check your credentials and try again. If you still cannot sign in, contact an administrator."
      );
    }

    return message;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    log.debug("LoginPage", "Login request:", {
      username,
      rememberMe,
    });
    e.preventDefault();
    setError("");
    const nextErrors = validateFields();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      window.requestAnimationFrame(() => {
        errorSummaryRef.current?.focus();
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await login({ username, password, rememberMe });
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? getFriendlyLoginError(err.message)
          : t("Failed to sign in. Please try again.")
      );
      // Announce and focus the server-side failure the same way field errors are.
      window.requestAnimationFrame(() => {
        serverErrorRef.current?.focus();
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAuthenticatedLogout = async () => {
    await logout();
    setUsername("");
    setPassword("");
  };

  if (!isLoading && isAuthenticated) {
    return (
      <AuthenticatedAuthNotice
        activeView="login"
        theme={appTheme}
        title={t("Already signed in")}
        description={t("Continue to Project Management or sign out to use a different account.")}
        continueLabel={t("Continue to app")}
        signOutLabel={t("Sign out")}
        onContinue={() => navigate(returnPath, { replace: true })}
        onSignOut={() => void handleAuthenticatedLogout()}
      />
    );
  }

  return (
    <AuthEntryShell
      activeView="login"
      dataAppReady={!isLoading && !isSubmitting}
      panelTitle={t("Sign in")}
      panelDescription=""
      switchPrompt={t("Need an account?")}
      switchLabel={t("Create an account")}
      switchTo={signupPath}
    >
      <form onSubmit={handleSubmit} className="min-w-0 space-y-6" noValidate>
        {summaryErrors.length > 0 && (
          <AuthErrorSummary
            ref={errorSummaryRef}
            titleId="login-error-summary-title"
            title={t("There is a problem")}
            items={summaryErrors}
          />
        )}

        {error && (
          <div
            ref={serverErrorRef}
            role="alert"
            tabIndex={-1}
            className="flex items-center gap-2 rounded-md bg-red-500/10 p-3 text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 dark:text-red-400"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <LoginFormInput
          id="username"
          type="text"
          label={t("Username or email")}
          value={username}
          onChange={(value) => {
            setUsername(value);
            setFieldErrors((current) => ({
              ...current,
              username: value.trim() ? undefined : current.username,
            }));
          }}
          placeholder={t("name@company.com")}
          icon={User}
          dir="auto"
          autoFocus
          autoComplete="username"
          error={fieldErrors.username}
          describedBy={fieldErrors.username ? "username-error" : undefined}
        />

        <LoginFormInput
          id="password"
          type="password"
          label={t("Password")}
          value={password}
          onChange={(value) => {
            setPassword(value);
            setFieldErrors((current) => ({
              ...current,
              password: value.trim() ? undefined : current.password,
            }));
          }}
          placeholder={t("Enter your password")}
          icon={Lock}
          autoComplete="current-password"
          error={fieldErrors.password}
          describedBy={fieldErrors.password ? "password-error" : undefined}
        />

        <label
          htmlFor="remember-me"
          className={cn("inline-flex cursor-pointer items-center gap-2 text-xs", appTheme.textSecondary)}
        >
          <input
            id="remember-me"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            aria-label={t("Remember me for 30 days")}
            className="h-4 w-4 shrink-0 rounded border-gray-300 accent-blue-600 focus:ring-2 focus:ring-zinc-500 dark:accent-notion-accent-blue"
          />
          <span>{t("Remember me for 30 days")}</span>
        </label>

        <button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            "h-11 min-h-[44px] w-full rounded-md px-4 py-2 font-medium transition-colors",
            appTheme.primaryAction,
            isSubmitting && "cursor-not-allowed opacity-70"
          )}
        >
          {isSubmitting ? t("Signing in...") : t("Sign in")}
        </button>
      </form>

    </AuthEntryShell>
  );
};
